import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { fetchThread, addReply, upvoteThread, upvoteReply, markBest, moderateThread, moderateReply } from "@/lib/api";
import AttachImages from "@/components/community/AttachImages";
import ThreadImages from "@/components/community/ThreadMedia";
import PollView from "@/components/community/PollView";
import { ArrowBigUp, ArrowLeft, CheckCircle2, Loader2, MessageSquare, EyeOff, Trash2, Award } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function ForumThread() {
  const { id } = useParams();
  const { pick } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [body, setBody] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState(false);

  const load = async () => {
    try {
      const d = await fetchThread(id);
      setThread(d.thread); setReplies(d.replies);
    } catch { setErr(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (err) return <div className="mx-auto max-w-2xl px-5 py-24 text-center text-ink-2"><T>This thread could not be found.</T></div>;
  if (!thread) return <div className="flex items-center gap-2 px-5 py-24 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> <T>Loading…</T></div>;

  const isQna = thread.kind === "qna";
  const inGroup = !!thread.group_id;
  const basePath = inGroup ? `/groups/${thread.group_id}` : isQna ? "/qna" : "/discussions";
  const backLabel = inGroup ? pick("समूह पर लौटें", "Back to group") : isQna ? pick("प्रश्न एवं उत्तर", "Questions & Answers") : pick("चर्चा मंच", "Discussions");
  const isAdmin = user?.role === "admin" || user?.role === "moderator";
  const isAuthor = user?.user_id === thread.author_id;

  const gate = () => { if (!user) { navigate("/login"); return false; } return true; };

  const doUpvoteThread = async () => {
    if (!gate()) return;
    const r = await upvoteThread(id);
    setThread({ ...thread, upvotes: r.upvotes, upvoted: r.upvoted });
  };
  const doUpvoteReply = async (rid) => {
    if (!gate()) return;
    const r = await upvoteReply(rid);
    setReplies(replies.map((x) => x.id === rid ? { ...x, upvotes: r.upvotes, upvoted: r.upvoted } : x));
  };
  const doBest = async (rid) => {
    if (!gate()) return;
    const r = await markBest(id, rid);
    setThread({ ...thread, best_answer_id: r.best_answer_id });
    setReplies(replies.map((x) => ({ ...x, is_best: x.id === r.best_answer_id })));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!gate() || (!body.trim() && !replyImages.length)) return;
    setPosting(true);
    try {
      await addReply(id, body, replyImages);
      setBody(""); setReplyImages([]);
      await load();
      toast(isQna ? "Answer posted" : "Reply posted", { description: "+5 sadhana points" });
    } catch (er) {
      toast("Could not post", { description: er?.response?.data?.detail || "Please try again." });
    } finally { setPosting(false); }
  };
  const modThread = async (action) => {
    if (action === "delete" && !window.confirm("Delete this thread and all its replies?")) return;
    await moderateThread(id, action);
    if (action === "delete") navigate(basePath); else toast(`Thread ${action === "hide" ? "hidden" : "restored"}`);
  };
  const modReply = async (rid, action) => {
    await moderateReply(rid, action);
    if (action === "delete") setReplies(replies.filter((x) => x.id !== rid));
    else toast(`Reply ${action}`);
  };

  return (
    <div data-testid="forum-thread-page" className="mx-auto max-w-3xl px-5 py-10 md:px-8">
      <Link to={basePath} className="inline-flex items-center gap-1.5 text-sm text-ink-2 transition-colors hover:text-saffron"><ArrowLeft className="h-4 w-4" /> {backLabel}</Link>

      <div className="mt-5 flex gap-4">
        <div className="flex w-12 shrink-0 flex-col items-center">
          <button onClick={doUpvoteThread} data-testid="thread-upvote" className={`transition-colors ${thread.upvoted ? "text-saffron" : "text-ink-3 hover:text-saffron"}`}><ArrowBigUp className="h-6 w-6" /></button>
          <span className="text-sm font-medium text-ink-2">{thread.upvotes}</span>
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-serif-latin text-2xl text-ink md:text-3xl">{thread.title}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-3">
            <span>{thread.author_name}</span><span>{timeAgo(thread.created_at)}</span>
            <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {replies.length}</span>
            {(thread.tags || []).map((tg) => <span key={tg} className="text-gold">#{tg}</span>)}
          </div>
          <p className="mt-4 whitespace-pre-wrap font-deva-body leading-relaxed text-ink-2">{thread.body}</p>
          <ThreadImages images={thread.images} />
          {thread.poll && <PollView threadId={thread.id} poll={thread.poll} />}
          {isAdmin && (
            <div className="mt-3 flex gap-2">
              <button onClick={() => modThread(thread.status === "hidden" ? "open" : "hide")} className="inline-flex items-center gap-1 border border-line px-2 py-1 text-xs text-ink-3 hover:text-saffron"><EyeOff className="h-3 w-3" /> {thread.status === "hidden" ? "Unhide" : "Hide"}</button>
              <button onClick={() => modThread("delete")} className="inline-flex items-center gap-1 border border-line px-2 py-1 text-xs text-red-600"><Trash2 className="h-3 w-3" /> <T>Delete</T></button>
            </div>
          )}
        </div>
      </div>

      <h2 className="mt-10 border-b border-line pb-2 font-serif-latin text-lg text-ink">
        {replies.length} {isQna ? pick("उत्तर", "Answers") : pick("प्रत्युत्तर", "Replies")}
      </h2>
      <div className="divide-y divide-line">
        {replies.map((r) => (
          <div key={r.id} data-testid={`reply-${r.id}`} className={`flex gap-4 py-5 ${r.is_best ? "bg-green-50/40 dark:bg-green-950/10" : ""}`}>
            <div className="flex w-10 shrink-0 flex-col items-center">
              <button onClick={() => doUpvoteReply(r.id)} data-testid={`reply-upvote-${r.id}`} className={`transition-colors ${r.upvoted ? "text-saffron" : "text-ink-3 hover:text-saffron"}`}><ArrowBigUp className="h-5 w-5" /></button>
              <span className="text-xs font-medium text-ink-2">{r.upvotes}</span>
            </div>
            <div className="min-w-0 flex-1">
              {r.is_best && <p className="mb-1 inline-flex items-center gap-1 text-xs font-medium text-green-600"><CheckCircle2 className="h-3.5 w-3.5" /> {pick("सर्वोत्तम उत्तर", "Best answer")}</p>}
              <p className="whitespace-pre-wrap font-deva-body leading-relaxed text-ink-2">{r.body}</p>
              <ThreadImages images={r.images} />
              <div className="mt-2 flex items-center gap-3 text-xs text-ink-3">
                <span>{r.author_name}</span><span>{timeAgo(r.created_at)}</span>
                {isQna && (isAuthor || isAdmin) && (
                  <button onClick={() => doBest(r.id)} data-testid={`mark-best-${r.id}`} className="inline-flex items-center gap-1 text-gold hover:text-saffron"><Award className="h-3.5 w-3.5" /> {r.is_best ? pick("हटाएँ", "Unmark") : pick("सर्वोत्तम", "Mark best")}</button>
                )}
                {isAdmin && <button onClick={() => modReply(r.id, "delete")} className="text-red-600"><T>Delete</T></button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="mt-8">
        {user ? (
          <>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="reply-input" rows={4}
              placeholder={isQna ? pick("अपना उत्तर लिखें…", "Write your answer…") : pick("अपना प्रत्युत्तर लिखें…", "Write a reply…")}
              className="w-full border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
            <div className="mt-3"><AttachImages images={replyImages} setImages={setReplyImages} max={4} /></div>
            <button type="submit" disabled={posting} data-testid="reply-submit" className="mt-3 inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {isQna ? pick("उत्तर दें", "Post answer") : pick("प्रत्युत्तर दें", "Post reply")}
            </button>
          </>
        ) : (
          <p className="border border-line bg-surface p-4 text-sm text-ink-2">
            <Link to="/login" className="text-saffron">{pick("लॉगिन करें", "Sign in")}</Link> {pick("उत्तर देने के लिए।", "to join the conversation.")}
          </p>
        )}
      </form>
    </div>
  );
}
